import React from "react";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Chip,
  Divider,
  Link,
  Spacer,
  Tab,
  Tabs,
} from "@heroui/react";
import { cn } from "@heroui/react";
import { Check } from "lucide-react";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";

import { FrequencyEnum, type Frequency, type Tier } from "./pricing-types";
import { frequencies, tiers } from "./pricing-tiers";
import { resolveSignupLinkHref, useTrialSignupHref } from "../../lib/signupUrl";
import PricingComparison from "./PricingComparison";

/** Motion 官方 Tilt Card：根据指针位置做 3D 倾斜，Hover/移动时生效 */
function TiltCard({
  children,
  className,
  /** 宽卡片用短边归一化，横纵与窄卡一致地打满倾斜 */
  tiltBasis = "edges",
}: {
  children: React.ReactNode;
  className?: string;
  tiltBasis?: "edges" | "min-side";
}) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const tiltXDeg = 6;
  const tiltYDeg = 2;
  const rotateX = useTransform(y, [-0.5, 0.5], [tiltXDeg, -tiltXDeg]);
  const rotateY = useTransform(x, [-0.5, 0.5], [-tiltYDeg, tiltYDeg]);

  const springConfig = { damping: 25, stiffness: 300 };
  const rotateXSpring = useSpring(rotateX, springConfig);
  const rotateYSpring = useSpring(rotateY, springConfig);

  const handlePointerMove = (e: React.PointerEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const halfW = rect.width / 2;
    const halfH = rect.height / 2;
    const basis = Math.max(Math.min(halfW, halfH), 1);
    const xNorm =
      tiltBasis === "min-side"
        ? (e.clientX - centerX) / basis
        : (e.clientX - centerX) / halfW;
    const yNorm =
      tiltBasis === "min-side"
        ? (e.clientY - centerY) / basis
        : (e.clientY - centerY) / halfH;
    x.set(Math.max(-1, Math.min(1, xNorm)));
    y.set(Math.max(-1, Math.min(1, yNorm)));
  };

  const handlePointerLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      className={className}
      style={{
        rotateX: rotateXSpring,
        rotateY: rotateYSpring,
        transformPerspective: 800,
      }}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      {children}
    </motion.div>
  );
}

function renderFeatureText(text: string) {
  const prefix = "月消息收发量 ";
  if (text.startsWith(prefix)) {
    const value = text.slice(prefix.length);
    return (
      <>
        {prefix}
        <span className="text-foreground font-bold">
          {value}
        </span>
      </>
    );
  }
  return text;
}

function PricingTierCard({
  tier,
  selectedFrequency,
  trialSignupHref,
}: {
  tier: Tier;
  selectedFrequency: Frequency;
  trialSignupHref: string;
}) {
  return (
    <TiltCard className="h-full">
      <Card
        isBlurred
        className={cn("h-full bg-background/60 dark:bg-default-100/50 p-3", {
          "border-small! border-primary/50": tier.mostPopular,
        })}
        shadow="md"
      >
        {tier.mostPopular ? (
          <Chip className="absolute top-4 right-4" color="primary" variant="flat">
            Most Popular
          </Chip>
        ) : null}
        <CardHeader className="flex flex-col items-start gap-2 pb-6">
          <h2 className="text-large font-medium">{tier.title}</h2>
          {tier.description ? (
            <p className="text-default-400 min-h-[4.5rem] text-left text-sm leading-relaxed">
              {tier.description}
            </p>
          ) : null}
        </CardHeader>
        <Divider />
        <CardBody className="gap-8">
          <p className="flex flex-wrap items-baseline gap-1 pt-2">
            <span className="text-foreground text-4xl leading-7 font-semibold tracking-tight">
              {typeof tier.price === "string"
                ? tier.price
                : tier.price[selectedFrequency.key]}
            </span>
            {typeof tier.price !== "string" ? (
              <span className="text-small text-default-400 font-medium">
                {tier.priceSuffix
                  ? `/${tier.priceSuffix}/${selectedFrequency.priceSuffix}`
                  : `/${selectedFrequency.priceSuffix}`}
              </span>
            ) : null}
          </p>
          {tier.featureSectionLabel ? (
            <p className="text-small text-default-500 -mt-2 font-medium text-left">{tier.featureSectionLabel}</p>
          ) : null}
          <ul className="flex flex-col gap-2">
            {tier.features?.map((feature) => (
              <li key={feature} className="flex items-center gap-2">
                <Check className="text-primary h-5 w-5 shrink-0" />
                <p className="text-default-500 text-sm text-left">{renderFeatureText(feature)}</p>
              </li>
            ))}
          </ul>
        </CardBody>
        <CardFooter>
          <Button
            fullWidth
            as={Link}
            color={tier.buttonColor ?? "default"}
            href={resolveSignupLinkHref(tier.href, trialSignupHref)}
            variant={tier.buttonVariant}
          >
            {tier.buttonText}
          </Button>
        </CardFooter>
      </Card>
    </TiltCard>
  );
}

type PricingSectionProps = {
  /** 首页为 false，仅价格页展示完整功能对比 */
  showFeatureComparison?: boolean;
};

export default function PricingSection({ showFeatureComparison = true }: PricingSectionProps) {
  const [selectedFrequency, setSelectedFrequency] = React.useState<Frequency>(frequencies[0]);
  const trialSignupHref = useTrialSignupHref("pricing");

  const onFrequencyChange = (selectedKey: React.Key) => {
    const frequencyIndex = frequencies.findIndex((f) => f.key === selectedKey);

    if (frequencyIndex !== -1) {
      setSelectedFrequency(frequencies[frequencyIndex]);
    }
  };

  return (
    <section
      id="pricing"
      className="relative flex w-full scroll-mt-[calc(var(--navbar-height)+var(--intl-notice-height,0px)+12px)] justify-center px-6 py-24 sm:py-32 lg:px-8 lg:py-40"
    >
      <div
        aria-hidden="true"
        className="px:5 absolute inset-x-0 top-3 z-0 h-full w-full transform-gpu overflow-hidden blur-3xl md:right-20 md:h-auto md:w-auto md:px-36"
      >
        <div
          className="mx-auto aspect-1155/678 w-288.75 bg-linear-to-tr from-[#00b7ff] to-[#00b7ff]/30 opacity-30"
          style={{
            clipPath:
              "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
          }}
        />
      </div>
      <div className="relative z-10 flex w-full max-w-6xl flex-col items-center text-center">
        <div className="flex max-w-xl flex-col text-center">
          <h2 className="text-foreground text-2xl leading-7 font-medium sm:text-4xl">价格</h2>
          {/* <h1 className="text-4xl font-medium tracking-tight">Get unlimited access.</h1> */}
          <Spacer y={4} />
          <h2 className="text-large text-default-500">
            多种版本满足您企业的使用需求
          </h2>
        </div>
        <Spacer y={8} />
        <Tabs
          classNames={{
            tabList: "bg-default-100/70",
            cursor: "bg-background dark:bg-default-200/30",
            tab: "data-[hover-unselected=true]:opacity-90",
          }}
          radius="full"
          onSelectionChange={onFrequencyChange}
        >
          <Tab
            key={FrequencyEnum.Yearly}
            aria-label="年付"
            className="pr-0.5"
            title={
              <div className="flex items-center gap-2">
                <p>年付</p>
                <Chip color="primary" variant="flat">
                  节省16%
                </Chip>
              </div>
            }
          />
          {/* <Tab key={FrequencyEnum.Quarterly} title="月付" /> */}
        </Tabs>
        <Spacer y={8} />
        <div className="flex w-full flex-col gap-4">
          <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {tiers.map((tier) => (
              <PricingTierCard
                key={tier.key}
                tier={tier}
                selectedFrequency={selectedFrequency}
                trialSignupHref={trialSignupHref}
              />
            ))}
          </div>
          {!showFeatureComparison ? (
            <div className="flex w-full justify-center">
              <Button
                as={RouterLink}
                className="border-default-100 text-small h-10 w-fit border-1 px-[16px] py-[10px] leading-5 font-medium transition-[background-color,border-color,color,box-shadow] duration-300 ease-out hover:border-default-400 hover:bg-default-100/60 hover:text-default-foreground hover:shadow-md hover:shadow-black/25"
                radius="full"
                to="/price"
                variant="bordered"
              >
                查看功能对比
              </Button>
            </div>
          ) : null}
        </div>
        {showFeatureComparison ? (
          <>
            <Spacer y={12} />
            <Divider className="mx-auto w-full max-w-5xl" />
            <PricingComparison />
          </>
        ) : null}
        {/* <div className="flex py-2">
          <p className="text-default-400 text-sm">
            Are you an open source developer?&nbsp;
            <Link href="#" className="text-foreground underline">
              Get a discount
            </Link>
          </p>
        </div> */}
      </div>
    </section>
  );
}
